import * as Sentry from '@sentry/browser';
import { PUBLISHER_EVENTS, PUBLISHER_STATUSES } from './enums';
import { setGetUserMediaCalled } from './lock';
import { PeerConnection } from './adapters';
import WebRTCBase from './base';
import generateUUID from './utils';
import * as whipwhap from './whip-whap';

// import consoleLog from './loggers';

export default class Publisher extends WebRTCBase {
  /**
   * Creates new instance of publisher with passed url and options
   * @param {string} streamUrl
   * @param {object} opts
   * @param {object} opts.preview
   * @param {object} opts.previewOptions - it's keys'll be applied to preview dom node as attributes
   * @param {boolean} opts.previewOptions.autoplay
   * @param {boolean} opts.previewOptions.controls
   * @param {boolean} opts.previewOptions.muted
   * @param {object} opts.constraints - video constraints
   * @param {boolean} opts.constraints.audio
   * @param {boolean} opts.constraints.video
   * @param {boolean} shouldLog
   * @param {boolean} opts.trackStats
   * @param {object} opts.statsContainer
   * @param {func} opts.returnCapabilities
   * @param {string} opts.password
   * @param {boolean} opts.authTokenName
   * @param {boolean} opts.tokenValue
   * @param {string} opts.sentryConfig - Sentry dsn address
   * @param {boolean} opts.whipwhap - enables WHIP/WHAP protocol (enabled by default)
   * @param {integer} opts.maxBitrate
   * canvas callback returning HTMLCanvasElement for adding video effects
   * @param {function} opts.canvasCallback
   * AMD Radeon low bitrate hack
   * @param {boolean} opts.radeonCheck
   * User streams from outside of publisher
   * @param {boolean} opts.inputStreams
   */
  constructor(streamUrl, opts = {}, shouldLog = false) {
    super(streamUrl, opts, shouldLog);

    const self = this;
    this.status = PUBLISHER_STATUSES.INITIALIZING;

    this.opts = opts;

    if (opts.preview) {
      if (opts.preview instanceof HTMLMediaElement) {
        this.videoContainer = opts.preview;
        if (opts.previewOptions) {
          this.videoContainer.autoplay = opts.previewOptions.autoplay !== false;
          this.videoContainer.controls = opts.previewOptions.controls || false;
          this.videoContainer.muted = opts.previewOptions.muted !== false;
        }
        this.videoContainer.addEventListener('loadedmetadata', function () {
          self.log(
            `Local video videoWidth: ${this.videoWidth}px,  videoHeight: ${this.videoHeight}px`,
          );
        });
      }
    }

    if ('maxBitrate' in opts) {
      this.maxBitrate = opts.maxBitrate;
    }

    if ('canvasCallback' in opts) {
      if (typeof opts.canvasCallback === 'function') {
        this.canvasCallback = opts.canvasCallback;
        this.forCanvasCallback =
          typeof document !== 'undefined' && document.createElement('canvas');
        this.forCanvasCallback.id = 'forCanvasCallback';
        this.forCanvasCallback.style.width = '320px';
        if (this.videoContainer) {
          this.videoContainer.after(this.forCanvasCallback);
          this.videoContainer.style.display = 'none';
        } else if (typeof document !== 'undefined') {
          document.body.appendChild(this.forCanvasCallback);
        }
        this.canvasCallback(this.forCanvasCallback);
        this.publishStreamFromCanvas = this.forCanvasCallback.captureStream();
      }
    }

    if (opts.sentryConfig && opts.sentryConfig.length) {
      this.sentryConfig = opts.sentryConfig;
    }

    if ('radeonCheck' in opts) {
      this.opts.radeonCheck = opts.radeonCheck;
    } else {
      this.opts.radeonCheck = true;
    }

    this.trackStats = opts.trackStats;
    this.statsContainer = opts.statsContainer;

    this.password = opts.password;

    this.constraints = opts.constraints;

    this.returnCapabilities = opts.returnCapabilities;
    const { userAgent } = typeof window !== 'undefined' && window.navigator;

    this.onEventCallback = opts.onEvent;

    this.statsSendEnable = opts.statsSendEnable || true;
    this.statsSendTime = opts.statsSendTime * 1000 || 60000;

    const supportsSetCodecPreferences =
      typeof window !== 'undefined' &&
      window.RTCRtpTransceiver &&
      'setCodecPreferences' in window.RTCRtpTransceiver.prototype;
    this.prefferedCodec = -1;

    // Chrome 95 hotfix
    if (userAgent.includes('Chrome/95')) {
      if (RTCRtpSender && supportsSetCodecPreferences) {
        const { codecs } = RTCRtpSender.getCapabilities('video');
        codecs.forEach((c, index) => {
          if (c.mimeType === 'video/H264' && c.sdpFmtpLine) {
            if (
              c.sdpFmtpLine.includes('profile-level-id=42e01f') &&
              c.sdpFmtpLine.includes('level-asymmetry-allowed=1')
            ) {
              if (c.sdpFmtpLine.includes('packetization-mode=0')) {
                this.prefferedCodec = index;
              } else if (
                c.sdpFmtpLine.includes('packetization-mode=1') &&
                this.prefferedCodec === -1
              ) {
                this.prefferedCodec = index;
              }
            } else if (
              c.sdpFmtpLine.includes('profile-level-id=42001f') &&
              c.sdpFmtpLine.includes('level-asymmetry-allowed=1') &&
              c.sdpFmtpLine.includes('packetization-mode=0') &&
              this.prefferedCodec === -1
            ) {
              this.prefferedCodec = index;
            }
          }
        });
      }
    }

    // Radeon Codec Hack
    if (this.opts.radeonCheck) {
      const GPU = this.getVideoCardInfo();
      this.log('Checking for GPU model:', GPU);
      if (
        GPU.length &&
        GPU.includes('AMD Radeon(TM) Graphics') &&
        RTCRtpSender &&
        supportsSetCodecPreferences
      ) {
        this.log('Detected AMD Radeon(TM) Graphics');
        // avaible h264 high bitrate codec
        this.prefferedCodec = 5;
      }
    }
  }

  /**
   * @param {object} opts
   * @param {object} opts.openPeerConnectionOptions
   * @param {object} opts.openPeerConnectionOptions.getMediaOptions
   * @param {function} opts.openPeerConnectionOptions.getMediaOptions.onGetUserMediaError
   */

  // Stats block start
  resetStats() {
    this.playerStatsObject = {
      proto: 'webrtc',
      user_agent: navigator.userAgent,
      bytes: 0,
      // player info
      application: {
        application_name: 'webrtc_publisher',
        application_version: WebRTCBase.version ? WebRTCBase.version : null,
        // counts
        stall_count: 0,
        pause_count: 0,
        error_count: 0,
        reconnect_count: 0,
        bitrate_change_count: 0,
        // durations
        live_duration: 0,
        stall_duration: 0,
        pause_duration: 0,
        // frames stats
        total_video_frames: 0,
        dropped_video_frames: 0,
        corrupted_video_frames: 0,
      },
    };
    if (this.statsSendEnable) {
      this.startSendStats();
    }
  }

  getUrl() {
    const url = new URL(this.url);
    url.protocol = 'https';
    url.pathname += '/whip';
    if (this.opts.authTokenName && this.opts.tokenValue) {
      if (this.password) {
        url.search = `?${this.opts.authTokenName}=${this.opts.tokenValue}&password=${this.password}`;
      } else {
        url.search = `?${this.opts.authTokenName}=${this.opts.tokenValue}`;
      }
    } else if (this.password) {
      url.search = `?password=${this.password}`;
    }
    return url.toString();
  }

  start() {
    // Stats
    this.resetStats();
    this.uuid = generateUUID();
    this.playerStatsObject.opened_at = Date.now();

    const url = this.getUrl();

    this.peerConnection = new PeerConnection();
    this.peerConnection.addEventListener(
      'iceconnectionstatechange',
      whipwhap.handleIceStateChange,
    );
    this.peerConnection.addEventListener('negotiationneeded', (ev) => {
      const headers = new Headers();
      headers.append('X-Sid', this.uuid);
      whipwhap.handleNegotiationNeeded(ev, url, {}, headers).then((result) => {
        this.xsid = result;
        this.playerStatsObject.started_at = Date.now();
        if (this.onEventCallback) {
          this.onEventCallback({ type: 'started', started_at: Date.now() });
        }
        this.playerStatsObject.updated_at = Date.now();
        this.emitter.emit(PUBLISHER_EVENTS.STREAMING);
        if (this.videoSender) {
          const { sender } = this.videoSender;
          const senderParams = sender.getParameters();
          this.log(
            'Publisher::maxBitrate is set to:',
            this.maxBitrate || 100000000,
          );
          senderParams.encodings[0].maxBitrate = this.maxBitrate || 100000000;
          senderParams.encodings[0].priority = 'medium';
          senderParams.encodings[0].networkPriority = 'medium';
          sender.setParameters(senderParams);
        }
        if (!this.progressListener) {
          this.startProgressListener();
        }
      });
    });

    /**
     * @param {object} opts
     * @param {object} opts.getMediaOptions
     * @param {function} opts.getMediaOptions.onGetUserMediaError
     * @param {object} config
     */
    this.getMedia(this.opts ? this.opts.getMediaOptions : null);
    this.startTrackStats();
    this.createSilentAudio(10, 44100);

    if (process.env.NODE_ENV === 'production' && this.sentryConfig) {
      Sentry.init({
        dsn: this.sentryConfig,
        environment: process.env.NODE_ENV || 'development',
        release: `FlussonicWebrtcPublisher${
          WebRTCBase.version ? `@${WebRTCBase.version}` : ''
        }`,

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
      });

      this.sentryEnabled = true;
    }
  }

  stop() {
    this.isFirstMsgReceived = false;
    this.clearLocalStreamSource();

    if (this.forCanvasCallback) {
      this.forCanvasCallback.remove();
      if (this.videoContainer) {
        this.videoContainer.style.display = 'block';
      }
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.status = PUBLISHER_STATUSES.STOPPED;

    // Stop tracking progress
    // if (this.stallingStarted) {
    //   this.durations.stall_duration += Date.now() - this.stallingStarted;
    //   delete this.stallingStarted;
    // }
    this.playerStatsObject.closed_at = Date.now();
    if (this.onEventCallback) {
      this.onEventCallback({ type: 'closed', closed_at: Date.now() });
    }
    this.stopSendStats();
    // this.removeListeners();
    if (this.progressListener) {
      clearInterval(this.progressListener);
      delete this.progressListener;
    }

    if (this.restartTimeout) {
      clearInterval(this.restartTimeout);
      delete this.restartTimeout;
    }

    if (this.trackStatsInterval) {
      clearInterval(this.trackStatsInterval);
      delete this.trackStatsInterval;
    }

    if (this.sharingStream) {
      this.sharingStream.getTracks().forEach((track) => track.stop());
      this.sharingStream = null;
    }
  }

  startTrackStats() {
    if (this.trackStats) {
      this.trackStatsInterval = window.setInterval(() => {
        if (this.peerConnection) {
          this.peerConnection.getStats(null).then((stats) => {
            let statsOutput = '';

            stats.forEach((report) => {
              statsOutput +=
                `<h2>Report: ${report.type}</h3>\n<strong>ID:</strong> ${report.id}<br>\n` +
                `<strong>Timestamp:</strong> ${report.timestamp}<br>\n`;

              // Now the statistics for this report; we intentially drop the ones we
              // sorted to the top above

              Object.keys(report).forEach((statName) => {
                if (
                  statName !== 'id' &&
                  statName !== 'timestamp' &&
                  statName !== 'type'
                ) {
                  statsOutput += `<strong>${statName}:</strong> ${report[statName]}<br>\n`;
                }
              });
            });

            if (this.statsContainer) {
              const statsBox = this.statsContainer;
              if (statsBox instanceof HTMLElement) {
                statsBox.innerHTML = statsOutput;
              }
            }
          });
        }
      }, 1000);
    }
  }

  clearLocalStreamSource() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  }

  /**
   * @param {object} opts
   * @param {function} opts.onGetUserMediaError
   * @param {function} [cb]
   */
  getMedia(opts, cb) {
    // this.clearLocalStreamSource();
    let constr = { ...this.constraints };
    if (!('video' in constr)) {
      constr = { ...constr, video: true };
    }
    if (this.opts.inputStreams) {
      this.gotMedia(this.opts.inputStreams);
    } else {
      this.getDevices().then((devices) => {
        this.activeDeviceList = devices;
        const currentAudioDevices = this.activeDeviceList.filter(
          (device) => device.kind === 'audioinput',
        );
        if (!currentAudioDevices.length) {
          constr.audio = false;
        }
        navigator.mediaDevices
          .getUserMedia(constr)
          .then((stream) => {
            setGetUserMediaCalled();
            this.log('Publisher::mediaDevices.getUserMedia success.');
            if (typeof cb === 'function') {
              cb(() => {
                this.log('Publisher::getMedia::cb');
                this.gotMedia(stream);
              });
            } else {
              this.gotMedia(stream);
            }

            if (this.videoContainer) {
              this.videoContainer.srcObject = this.stream;
            }
          })
          .catch((error) => {
            this.log(
              'Publisher::mediaDevices.getUserMedia exception occured.',
              error,
            );
            if (this.opts && this.opts.onGetUserMediaError) {
              this.opts.onGetUserMediaError(error);
            }
            this.sendErrorLog();
          });
      });
    }

    const startDummy = () => {
      this.dummyVideo = this.createDummyVideo('dummyVideo');
      this.dummyVideo.srcObject = this.stream;
      this.dummyVideo.addEventListener(
        'loadedmetadata',
        this.updateCanvasSize.bind(this),
        false,
      );
      this.dummyVideo.addEventListener(
        'play',
        () => {
          requestAnimationFrame(this.updateCanvas.bind(this));
        },
        false,
      );
    };

    if (this.forCanvasCallback) {
      startDummy();
      this.canvasCallbackContext = this.forCanvasCallback.getContext('2d');
    }

    navigator.mediaDevices.ondevicechange = this.onDeviceChange.bind(this);
  }

  updateCanvasSize() {
    this.forCanvasCallback.width = this.dummyVideo.videoWidth;
    this.forCanvasCallback.height = this.dummyVideo.videoHeight;
  }

  updateCanvas() {
    if (this.dummyVideo.ended || this.dummyVideo.paused) {
      return;
    }

    if (this.forCanvasCallback && this.canvasCallbackContext) {
      this.canvasCallbackContext.drawImage(
        this.dummyVideo,
        0,
        0,
        this.dummyVideo.videoWidth,
        this.dummyVideo.videoHeight,
      );
    }

    requestAnimationFrame(this.updateCanvas.bind(this));
  }

  async getDevices() {
    // Получаем список устройств
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      this.log('enumerateDevices() not supported.');
      return;
    }

    // List cameras and microphones.
    let deviceList = [];

    await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        deviceList = devices;
        return deviceList;
      })
      .catch((error) => {
        this.log(
          'Publisher::mediaDevices.getUserMedia exception occured.',
          error,
        );
        if (this.opts && this.opts.onGetUserMediaError) {
          this.opts.onGetUserMediaError(error);
        }
        this.sendErrorLog();
      });

    const inputDevices = deviceList.filter(
      (device) => device.kind === 'audioinput' || device.kind === 'videoinput',
    );
    this.log('Publisher:: Devices list ', inputDevices);

    // const supportedConstraints =
    //   navigator.mediaDevices.getSupportedConstraints();

    // eslint-disable-next-line consistent-return
    return deviceList;
  }

  onDeviceChange(event) {
    this.addPlayerStat('bitrate_change_count');
    // Если поменялась конфигурация устройств
    this.log('Publisher::Device configuration changed', event);
    let aviableAudioDevices = [];
    // let aviableVideoDevices = [];

    this.getDevices.bind(this);
    this.getDevices().then((devices) => {
      aviableAudioDevices = devices.filter(
        (device) => device.kind === 'audioinput',
      );
      // aviableVideoDevices = devices.filter(
      //   device => device.kind === 'videoinput',
      // );
      if (!this.audioSender || !this.audioSender.sender) return;
      if (!aviableAudioDevices.length) {
        this.log('Publisher::Audio input devices lost');
        const tracks = this.destination.stream.getAudioTracks();
        this.audioSender.sender.replaceTrack(tracks[0]);
      } else if (aviableAudioDevices.length !== 0) {
        this.log('Publisher::New audio input device discovered');
        navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
          const audioTracks = stream.getAudioTracks();
          if (audioTracks.length > 0) {
            this.log(`Using audio device: ${audioTracks[0].label}`);
            this.audioSender.sender.replaceTrack(audioTracks[0]);
          }
        });
      }
      this.activeDeviceList = devices;
    });
  }

  gotMedia(stream) {
    this.stream = stream;
    this.log('Publisher::got', this.stream);
    this.log('Publisher::gotMedia::peerConnection', this.peerConnection);
    let hasVideo = false;
    if ('video' in this.constraints) {
      hasVideo = this.constraints.video;
    }
    if (this.publishStreamFromCanvas) {
      const [canvasStream] = this.publishStreamFromCanvas.getVideoTracks();
      this.videoTrack = canvasStream;
      this.videoSender = this.peerConnection.addTransceiver(this.videoTrack, {
        direction: 'sendonly',
      });
    } else if (hasVideo) {
      const [publishVideoTrack] = stream.getVideoTracks();
      this.videoTrack = publishVideoTrack;
      this.videoTrack.contentHint = 'detail';
      this.videoSender = this.peerConnection.addTransceiver(this.videoTrack, {
        direction: 'sendonly',
      });
    }
    this.audioSender = this.peerConnection.addTransceiver(
      stream.getAudioTracks()[0],
      {
        direction: 'sendonly',
      },
    );

    if (this.prefferedCodec !== -1) {
      let { codecs } = RTCRtpSender.getCapabilities('video');
      const selectedCodec = codecs[this.prefferedCodec];
      this.log(
        'Publisher preffered codec:',
        this.prefferedCodec,
        selectedCodec,
      );
      codecs = [selectedCodec];
      const transceiver = this.peerConnection
        .getTransceivers()
        .find((t) => t.sender && t.sender.track === this.videoTrack);
      transceiver.setCodecPreferences(codecs);
    }
    this.peerConnection.onconnectionstatechange = (event) => {
      this.log(
        'Publisher::peer connection state',
        this.peerConnection.connectionState,
        event,
      );
      if (this.onEventCallback) {
        this.onEventCallback({
          type: 'connection state',
          connection: this.peerConnection.connectionState,
        });
      }
    };
  }

  restart() {
    this.addPlayerStat('reconnect_count');
    this.stop();
    this.restartTimeout = setTimeout(this.start.bind(this), 1000);
  }

  mute() {
    this.stream.getTracks().forEach((track) => {
      if (track.kind === 'audio') {
        track.enabled = !track.enabled;
      }
    });
  }

  switchTrack(track) {
    if (track instanceof MediaStreamTrack) {
      const { kind } = track;
      const isVideo = kind === 'video';
      if (isVideo) {
        if (!this.videoSender || !this.videoSender.sender) return;
        this.videoSender.sender.replaceTrack(track);
      } else {
        if (!this.audioSender || !this.audioSender.sender) return;
        this.audioSender.sender.replaceTrack(track);
      }
    } else {
      console.info(
        'Publisher only get stream tracks to switch RTCRtpTransceiver sender',
      );
    }
  }

  shareScreen() {
    if (!this.sharingStream) {
      const displayMediaOptions = {
        video: {
          cursor: 'always',
        },
        audio: false,
      };

      navigator.mediaDevices
        .getDisplayMedia(displayMediaOptions)
        .then((stream) => {
          this.sharingStream = stream;
          this.videoContainer.srcObject = stream;
          const [shareScreenTrack] = this.sharingStream.getVideoTracks();
          this.sharingScreen = shareScreenTrack;
          if (!this.videoSender || !this.videoSender.sender) return;
          this.videoSender.sender.replaceTrack(this.sharingScreen);
        })
        .catch((err) => {
          this.log('Publisher:: screen sharing error', err);
        });
    } else {
      if (!this.videoSender || !this.videoSender.sender) return;
      this.videoSender.sender.replaceTrack(this.videoTrack);
      this.videoContainer.srcObject = this.stream;
      this.sharingStream.getTracks().forEach((track) => track.stop());
      this.sharingStream = null;
    }
  }

  // audioEnded(e) {
  //   const { currentTarget } = e;
  //   currentTarget.enabled = false;
  // }

  createSilentAudio(time, freq = 44100) {
    const length = time * freq;
    const AudioContext =
      typeof window !== 'undefined' &&
      (window.AudioContext ||
        window.webkitAudioContext ||
        window.mozAudioContext);
    if (!AudioContext) {
      this.log('No Audio Context');
    }
    const context = new AudioContext();
    const audioFile = context.createBuffer(1, length, freq);
    this.destination = context.createMediaStreamDestination();

    this.silentSource = context.createBufferSource();
    this.silentSource.buffer = audioFile;
    this.silentSource.connect(context.destination);
    this.silentSource.loop = true;
    this.silentSource.start();
    this.silentSource.connect(this.destination);
  }

  sendErrorLog(error) {
    if (this.sentryEnabled) {
      Sentry.captureException(error);
    }
  }

  createDummyVideo(elName) {
    const creatibleEl =
      typeof document !== 'undefined' && document.createElement('video');
    if (typeof document !== 'undefined') {
      document.querySelector('body').appendChild(creatibleEl);
    }
    creatibleEl.setAttribute('id', elName);
    creatibleEl.setAttribute('autoplay', true);
    creatibleEl.style.position = 'absolute';
    creatibleEl.style.top = 0;
    creatibleEl.style.left = 0;
    creatibleEl.style.zIndex = '-1';
    creatibleEl.style.opacity = 0;
    creatibleEl.style.width = '1px';
    creatibleEl.style.height = '1px';
    creatibleEl.style.overflow = 'hidden';
    creatibleEl.muted = true;
    return creatibleEl;
  }

  // Stats block

  startProgressListener() {
    this.progressListener = setInterval(() => {
      if (this.peerConnection) {
        this.peerConnection.getStats(null).then((stats) => {
          const statsOutput = [];

          stats.forEach((report) => {
            statsOutput.push({
              type: report.type,
              id: report.id,
              timestamp: report.timestamp,
            });

            if (report.type === 'transport') {
              const {
                // bytesReceived,
                timestamp,
                bytesSent,
                // packetsReceived,
                // packetsSent,
              } = report;
              this.playerStatsObject.bytes = bytesSent;
              this.playerStatsObject.updated_at = Math.round(timestamp);

              if (this.playbackSegmentStart) {
                const { application } = this.playerStatsObject;
                if (application) {
                  let { live_duration } = application;
                  live_duration += timestamp - this.playbackSegmentStart;
                  this.addPlayerStat(
                    'live_duration',
                    Math.round(live_duration),
                  );
                  delete this.playbackSegmentStart;
                }
              }
              this.playbackSegmentStart = timestamp;
            }
            // else if (report.type === 'track') {
            //   if ('kind' in report) {
            //     if (report.kind === 'video') {
            //       this.addPlayerStat(
            //         'total_video_frames',
            //         false,
            //         report.framesSent,
            //       );
            //       // this.addPlayerStat(
            //       //   'dropped_video_frames',
            //       //   false,
            //       //   report.framesDropped,
            //       // );
            //       // this.addPlayerStat(
            //       //   'corrupted_video_frames',
            //       //   false,
            //       //   corruptedVideoFrames,
            //       // );
            //     }
            //   }
            // }
          });
        });
      }
    }, 1000);
  }

  addPlayerStat(name, data = null) {
    const { application } = this.playerStatsObject;
    if (application) {
      if (data) {
        application[name] = Math.round(data);
      } else {
        application[name] += 1;
      }
      this.playerStatsObject.application = application;
    }
  }

  getStats() {
    return {
      ...this.playerStatsObject,
      id: this.uuid,
    };
  }

  startSendStats() {
    this.sendingInterval = setInterval(() => {
      this.postStats();
    }, this.statsSendTime);
  }

  stopSendStats() {
    if (!this.sendingInterval) {
      return;
    }
    clearInterval(this.sendingInterval);
    this.postStats();
  }

  getStatsUrl() {
    const authToken =
      this.opts.authTokenName && this.opts.tokenValue
        ? `?${this.opts.authTokenName}=${this.opts.tokenValue}`
        : '';

    return `${this.url}/sessions/${this.xsid ? this.xsid : ''}${authToken}`;
  }

  postStats() {
    if (!this.playerStatsObject.started_at) {
      return;
    }

    fetch(this.getStatsUrl(), {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(this.getStats()),
    });
  }

  userIsChangingTracks(e, data) {
    console.log('userIsChangingTracks', { e, data });
  }

  /**
   *
   * @param {array} args
   * @param {string} args[0] - event name
   * @param {function} args[1] - callback for event
   * @returns {*}
   */
  on(...args) {
    const { on } = this.emitter;
    return on.apply(this.emitter, args);
  }
}
